/* BookingWidget → ServicePro booking API client.
 *
 * Reuses the same public booking endpoints the standalone booking app
 * (apps/booking) calls — only the contracts are shared, not the UI. `apiUrl`
 * is passed in from the injected jobsite runtime config (window.__SERVICEPRO_CONFIG__).
 * Each call returns a discriminated result so callers render inline errors
 * without try/catch.
 */

import type { Data } from "@puckeditor/core";
import { EMPTY_FORM_DATA } from "../../forms/form-data";
import { HTTP_STATUS } from "@servicepro/utils";

type BookingSchedulingMode = "instant" | "request";

interface BookingService {
  id: string;
  name: string;
  slug: string;
  blurb: string;
  duration: number | null; // Minutes
  price: number | null; // Dollars; null → "Estimate"
  priceQualifier: string | null;
  deposit: number | null;
  popular: boolean;
  urgent: boolean;
  schedulingMode: BookingSchedulingMode;
}

interface BookingPro {
  name: string;
  slug: string;
  trade: string | null;
  area: string | null;
  phone: string | null;
}

interface BookingPublicData {
  pro: BookingPro;
  services: BookingService[];
  /** The booking form's Puck document (empty → default widget). */
  formData: Data;
}

/** `offline` separates "the API is down" from "the API said no" — only the first may park a lead. */
const postBooking = async (
  apiUrl: string,
  path: string,
  body: unknown,
): Promise<{ ok: boolean; offline: boolean; data: Record<string, unknown> }> => {
  try {
    const res = await fetch(`${apiUrl}/api/booking${path}`, {
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      }),
      data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { data, offline: res.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR, ok: res.ok };
  } catch {
    return { data: {}, offline: true, ok: false };
  }
};

const createBooking = async (
    apiUrl: string,
    body: CreateBookingBody,
  ): Promise<{ ok: true; booking: BookingResult } | { ok: false; offline: boolean; error: string }> => {
    const { ok, offline, data } = await postBooking(apiUrl, "", body);
    if (ok) {
      return { booking: data.booking as BookingResult, ok: true };
    }
    return { error: (data.error as string) ?? "Couldn't complete your booking.", offline, ok: false };
  },

  /**
   * Bookable times for one instant-mode service. Separate from the page payload
   * because it goes stale the moment anyone books.
   */
  fetchBookingAvailability = async (
    apiUrl: string,
    slug: string,
    serviceId: string,
    days = 14,
  ): Promise<
    { ok: true; timeZone: string; days: BookingAvailabilityDay[] } | { ok: false; error: string }
  > => {
    try {
      const url =
          `${apiUrl}/api/booking/public/${encodeURIComponent(slug)}/availability` +
          `?serviceId=${encodeURIComponent(serviceId)}&days=${days}`,
        res = await fetch(url),
        json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        return { error: (json.error as string) ?? "Couldn't load available times.", ok: false };
      }
      return {
        days: (json.days as BookingAvailabilityDay[]) ?? [],
        ok: true,
        timeZone: json.timeZone as string,
      };
    } catch {
      return { error: "Couldn't load available times.", ok: false };
    }
  },

  fetchBookingPublic = async (
    apiUrl: string,
    slug: string,
  ): Promise<{ ok: true; data: BookingPublicData } | { ok: false; offline: boolean }> => {
    try {
      const res = await fetch(`${apiUrl}/api/booking/public/${encodeURIComponent(slug)}`);
      if (!res.ok) {
        return { offline: res.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR, ok: false };
      }
      const json = (await res.json()) as BookingPublicData;
      return {
        data: {
          formData: json.formData ?? EMPTY_FORM_DATA,
          pro: json.pro,
          services: json.services ?? [],
        },
        ok: true,
      };
    } catch {
      return { offline: true, ok: false };
    }
  },
  sendOtp = async (
    apiUrl: string,
    phone: string,
    opts: { slug?: string; turnstileToken?: string } = {},
  ): Promise<{ ok: true } | { ok: false; offline: boolean; error: string; code?: string }> => {
    // Slug lets the server verify the jobsite's per-site Turnstile token.
    const { ok, offline, data } = await postBooking(apiUrl, "/otp/send", {
      phone,
      slug: opts.slug,
      turnstileToken: opts.turnstileToken,
    });
    if (ok) {
      return { ok: true };
    }
    return {
      code: data.code as string | undefined,
      error: (data.error as string) ?? "Couldn't send a code. Please try again.",
      offline,
      ok: false,
    };
  },
  /**
   * Upload one form photo: ask our API for a one-time Cloudflare direct-upload URL
   * (plan-gated, rate-limited), PUT the file to Cloudflare, return its public URL.
   */
  uploadFormImage = async (
    apiUrl: string,
    slug: string,
    file: File,
  ): Promise<{ ok: true; url: string } | { ok: false; error: string }> => {
    try {
      const init = await fetch(`${apiUrl}/api/forms/upload-url`, {
          body: JSON.stringify({ slug }),
          headers: { "Content-Type": "application/json" },
          method: "POST",
        }),
        initData = (await init.json().catch(() => ({}))) as Record<string, unknown>;
      if (!init.ok) {
        return { error: (initData.error as string) ?? "Couldn't upload that photo.", ok: false };
      }

      const form = new FormData();
      form.append("file", file);
      const up = await fetch(initData.uploadUrl as string, { body: form, method: "POST" });
      if (!up.ok) {
        return { error: "Couldn't upload that photo.", ok: false };
      }
      return { ok: true, url: initData.publicUrl as string };
    } catch {
      return { error: "Couldn't upload that photo.", ok: false };
    }
  },
  verifyOtp = async (
    apiUrl: string,
    phone: string,
    code: string,
  ): Promise<
    | { ok: true; verificationId: string; customer: VerifiedCustomer }
    | { ok: false; offline: boolean; error: string }
  > => {
    const { ok, offline, data } = await postBooking(apiUrl, "/otp/verify", { code, phone });
    if (ok) {
      return {
        customer: data.customer as VerifiedCustomer,
        ok: true,
        verificationId: data.verificationId as string,
      };
    }
    return { error: (data.error as string) ?? "Couldn't verify that code.", offline, ok: false };
  };

interface VerifiedCustomer {
  exists: boolean;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

interface CreateBookingBody {
  verificationId: string;
  slug: string;
  serviceId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  consent: boolean;
  /** Ticked "Text me updates about this booking". Optional, never required to book. */
  smsOptIn?: boolean;
  /** Instant mode — an exact start time taken from the availability list. */
  slot?: { startsAt: string };
  request?: { preferredDate?: string; window: string; note?: string };
  /** Form-builder custom field values, keyed by field.key. Stored in formData. */
  customFields?: Record<string, unknown>;
  /** One-line service address, as the customer gave it. */
  address?: string;
  sessionId?: string | null;
}

interface BookingResult {
  id: string;
  status: string;
  confCode: string;
  serviceName: string;
}

interface BookingAvailabilityDay {
  /** Local calendar day on the business's timezone, as YYYY-MM-DD. */
  date: string;
  isWorkingDay: boolean;
  slots: { startsAt: string; minute: number; available: boolean }[];
}

export type {
  BookingSchedulingMode,
  BookingService,
  BookingPro,
  BookingPublicData,
  VerifiedCustomer,
  CreateBookingBody,
  BookingResult,
  BookingAvailabilityDay,
};
export {
  fetchBookingPublic,
  uploadFormImage,
  sendOtp,
  verifyOtp,
  createBooking,
  fetchBookingAvailability,
};
